import { useMemo } from 'react';
import { transformDataForTable, transformVisibilityResultsForTable } from 'utils/agendaRecord/agendaRecord.ts';
import type { AgendaRecordItem, GetAgendaRecordsRes, InlineRecordType } from 'types';
import type { VisibilityResultWithName } from 'utils/validations/multipleDefinitions';

interface UseAgendaTableDataProps {
  data: GetAgendaRecordsRes<AgendaRecordItem[]>;
  shouldSkipFetch?: boolean;
  overrideValues?: InlineRecordType[];
  calculatedValues?: Record<string, unknown>;
  calculatedVisibility?: Record<string, VisibilityResultWithName>;
}

export const useAgendaTableData = ({ data, shouldSkipFetch, overrideValues, calculatedValues, calculatedVisibility }: UseAgendaTableDataProps) => {
  const transformedData = useMemo(() => {
    if (shouldSkipFetch) return [];

    const baseData = transformDataForTable(data.items, overrideValues);

    if (!calculatedValues || Object.keys(calculatedValues).length === 0) {
      return baseData;
    }

    return baseData.map((row) => {
      const calculatedRowData = calculatedValues[row.identifier];
      if (calculatedRowData) {
        return {
          ...row,
          ...calculatedRowData,
        };
      }
      return row;
    });
  }, [data, shouldSkipFetch, overrideValues, calculatedValues]);

  const visibilityResults = useMemo(() => {
    const baseVisibilityResults = transformVisibilityResultsForTable(data);

    if (!calculatedVisibility || Object.keys(calculatedVisibility).length === 0) {
      return baseVisibilityResults;
    }

    return { ...baseVisibilityResults, ...calculatedVisibility };
  }, [data, calculatedVisibility]);

  return { transformedData, visibilityResults };
};
